import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSignatureCanvas } from '~/modules/signature/hooks/useSignatureCanvas';
import { useAtom } from 'jotai';
import { signatureDataAtom } from '~/modules/ecommerce/states/accept-quote-popup-atoms';
import { calculatePixelCount } from '~/utils';
import { cn } from '@rocco/index';
import { useLoadFont } from '~/hooks/use-load-font';

const availableFonts = [
    { label: 'Allura (smooth)', family: "'Allura', cursive", gf: 'Allura' },
    {
        label: 'Great Vibes (elegant)',
        family: "'Great Vibes', cursive",
        gf: 'Great+Vibes',
    },
    {
        label: 'Dancing Script (casual)',
        family: "'Dancing Script', cursive",
        gf: 'Dancing+Script:wght@400..700',
    },
    {
        label: 'Satisfy (lively)',
        family: "'Satisfy', cursive",
        gf: 'Satisfy',
    },
    {
        label: 'Pacifico (playful)',
        family: "'Pacifico', cursive",
        gf: 'Pacifico',
    },
];

interface SignatureCanvasProps {
    /** The name to display as typed signature */
    name: string;
    /** Whether the signature canvas is visible/active */
    isVisible: boolean;
    /** Canvas height in pixels */
    canvasHeight: number;
    /** Canvas maximum width in pixels */
    canvasMaxWidth: number;
    /** Optional className for styling */
    className?: string;
    onlySignatureFamily?: string;
}

/**
 * Reusable signature canvas component that supports both typed and drawn signatures
 * Provides a canvas for users to either type their name in various fonts or draw their signature
 * Load font manually can keep better performance (using remix links)
 */
export const SignatureCanvas = ({
    name,
    isVisible,
    canvasHeight,
    canvasMaxWidth,
    className = '',
    onlySignatureFamily,
}: SignatureCanvasProps) => {
    // Use the custom hook for signature business logic
    const {
        useDrawn,
        fontFamily,
        setFontFamily,
        toggleSignatureMode,
        clearCanvas,
        setClearCanvasRef,
    } = useSignatureCanvas({
        name,
    });

    // Use onlySignatureFamily if provided, otherwise use the selected fontFamily
    const effectiveFontFamily = onlySignatureFamily || fontFamily;

    // Use atom for signature data
    const [, setSignatureData] = useAtom(signatureDataAtom);

    // Canvas refs and state
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const drawingRef = useRef(false);
    const hasInkRef = useRef(false);
    const dprRef = useRef(1);

    // Internal signature data state
    const [currentSignature, setCurrentSignature] = useState<{
        hasSignature: boolean;
        dataUrl: string;
        pixelCount: number;
    }>({ hasSignature: false, dataUrl: '', pixelCount: 0 });

    // Ref to store current signature for the hook
    const currentSignatureRef = useRef(currentSignature);

    // Internal setSignature function that updates internal state and hook state
    const setSignature = useCallback(
        (dataUrl: string) => {
            const canvas = canvasRef.current;
            if (!canvas) {
                const emptySignature = {
                    hasSignature: false,
                    dataUrl: '',
                    pixelCount: 0,
                };
                setCurrentSignature(emptySignature);
                currentSignatureRef.current = emptySignature;
                setSignatureData(emptySignature);
                return;
            }

            const pixelCount = calculatePixelCount(canvas, '#ffffff');
            const hasSignature = pixelCount > 0;
            const newSignature = { hasSignature, dataUrl, pixelCount };

            setCurrentSignature(newSignature);
            currentSignatureRef.current = newSignature;
            setSignatureData(newSignature);
        },
        [setSignatureData],
    );

    const { loadFont } = useLoadFont();

    const ensureFontLoaded = useCallback(
        async (familyCss: string) => {
            const found = availableFonts.find((f) => f.family === familyCss);
            if (found) {
                await loadFont(familyCss, found.gf);
            }
        },
        [loadFont],
    );

    // Canvas helper functions
    const getPos = (
        e:
            | React.PointerEvent<HTMLCanvasElement>
            | React.MouseEvent<HTMLCanvasElement>
            | React.TouchEvent<HTMLCanvasElement>
            | globalThis.Touch,
    ) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        let clientX: number, clientY: number;

        if ('touches' in e) {
            // Touch event
            const touch = e.touches?.[0] || e;
            clientX = (touch as globalThis.Touch).clientX;
            clientY = (touch as globalThis.Touch).clientY;
        } else {
            // Mouse or pointer event
            clientX = (
                e as
                    | React.MouseEvent<HTMLCanvasElement>
                    | React.PointerEvent<HTMLCanvasElement>
            ).clientX;
            clientY = (
                e as
                    | React.MouseEvent<HTMLCanvasElement>
                    | React.PointerEvent<HTMLCanvasElement>
            ).clientY;
        }

        const x = clientX - rect.left;
        const y = clientY - rect.top;
        return { x, y };
    };

    const clearCanvasInternal = useCallback(
        (shouldNotifyChange = true) => {
            const canvas = canvasRef.current;
            if (!canvas) {
                return;
            }

            // Force complete canvas reset by recreating the context
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }

            // Store current dimensions
            const width = canvas.width;
            const height = canvas.height;

            // Completely clear the canvas by resetting its dimensions
            canvas.width = width;
            canvas.height = height;

            // Set up the context from scratch
            const dpr = dprRef.current;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = 3;
            ctx.strokeStyle = '#0f172a';
            ctx.fillStyle = '#ffffff';

            // Fill with white background
            ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);

            // Update the context ref
            ctxRef.current = ctx;

            // Reset the drawing state
            hasInkRef.current = false;
            drawingRef.current = false;

            if (shouldNotifyChange) {
                const dataUrl = canvas.toDataURL('image/png');
                setSignature(dataUrl);
            }
        },
        [setSignature],
    );

    const drawTypedSignature = useCallback(async () => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) {
            return;
        }
        if (!nameRef.current.trim()) {
            return;
        }

        // Wait for font to actually load before proceeding
        await ensureFontLoadedRef.current(fontFamilyRef.current);

        // Set up text properties
        ctx.fillStyle = '#0f172a';
        ctx.textBaseline = 'middle';

        const padding = 24;
        const maxWidth = canvas.clientWidth - padding * 2;
        let size = 64;

        // Try different font string formats to find one that works
        const fontStringOptions = [
            `italic ${size}px ${fontFamilyRef.current}`,
            `italic ${size}px Allura`,
            `italic ${size}px "Allura"`,
            `${size}px Allura`,
        ];

        let workingFontString = fontStringOptions[0];

        // Test which font string actually works
        for (const fontString of fontStringOptions) {
            ctx.font = fontString;
            const testWidth = ctx.measureText('Test').width;

            // Check if the font is actually Allura by comparing with a known fallback
            ctx.font = 'italic 64px serif';
            const fallbackWidth = ctx.measureText('Test').width;
            ctx.font = fontString;
            const alluraWidth = ctx.measureText('Test').width;

            // If the font is working and different from fallback, use it
            if (
                testWidth > 0 &&
                testWidth < 1000 &&
                Math.abs(fallbackWidth - alluraWidth) > 5
            ) {
                workingFontString = fontString;
                break;
            }
        }

        ctx.font = workingFontString || `italic ${size}px Allura`;
        let width = ctx.measureText(nameRef.current).width;

        while (width > maxWidth && size > 20) {
            size -= 2;
            // Use the same font format that worked for the initial size
            const newFontString =
                workingFontString?.replace(/\d+px/, `${size}px`) ||
                `italic ${size}px Allura`;
            ctx.font = newFontString;
            width = ctx.measureText(nameRef.current).width;
        }

        const x = (canvas.clientWidth - width) / 2;
        const y = canvas.clientHeight / 2;
        ctx.fillText(nameRef.current, x, y);

        hasInkRef.current = true;
        const dataUrl = canvas.toDataURL('image/png');
        setSignature(dataUrl);
    }, [setSignature]);

    const start = useCallback(
        (x: number, y: number) => {
            const ctx = ctxRef.current;
            if (!ctx) {
                return;
            }
            drawingRef.current = true;
            hasInkRef.current = true;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x, y);
            ctx.stroke();
            const canvas = canvasRef.current;
            if (canvas) {
                const dataUrl = canvas.toDataURL('image/png');
                setSignature(dataUrl);
            }
        },
        [setSignature],
    );

    const move = useCallback(
        (x: number, y: number) => {
            const ctx = ctxRef.current;
            if (!ctx || !drawingRef.current) {
                return;
            }
            ctx.lineTo(x, y);
            ctx.stroke();
            const canvas = canvasRef.current;
            if (canvas) {
                const dataUrl = canvas.toDataURL('image/png');
                setSignature(dataUrl);
            }
        },
        [setSignature],
    );

    const end = useCallback(() => {
        const ctx = ctxRef.current;
        if (!ctx) return;
        drawingRef.current = false;
        ctx.beginPath();
    }, []);

    const renderSignature = useCallback(async () => {
        // Only render typed signature when in typed mode and have a name to draw
        if (!useDrawnRef.current && nameRef.current.trim()) {
            clearCanvasInternal(false); // Don't notify change yet
            // Small delay to ensure clearing is complete
            await new Promise((resolve) => setTimeout(resolve, 10));
            await drawTypedSignatureRef.current();
        }
        // For drawn mode, don't do anything here - the canvas clearing is handled by toggleSignatureMode
    }, [clearCanvasInternal]);

    // Use refs to avoid dependency issues
    const renderSignatureRef = useRef(renderSignature);
    renderSignatureRef.current = renderSignature;
    const clearCanvasInternalRef = useRef(clearCanvasInternal);
    clearCanvasInternalRef.current = clearCanvasInternal;
    const drawTypedSignatureRef = useRef(drawTypedSignature);
    drawTypedSignatureRef.current = drawTypedSignature;

    // Refs for values used in callbacks
    const nameRef = useRef(name);
    const fontFamilyRef = useRef(effectiveFontFamily);
    const useDrawnRef = useRef(useDrawn);
    const ensureFontLoadedRef = useRef(ensureFontLoaded);

    // Update refs when values change
    nameRef.current = name;
    fontFamilyRef.current = effectiveFontFamily;
    useDrawnRef.current = useDrawn;
    ensureFontLoadedRef.current = ensureFontLoaded;

    // Set up the clear canvas ref for the hook (only once when component mounts)
    useEffect(() => {
        setClearCanvasRef(clearCanvasInternal);
    }, [clearCanvasInternal, setClearCanvasRef]);

    // Set up touch event listeners with passive: false to allow preventDefault
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const handleTouchStart = (e: TouchEvent) => {
            if (!useDrawnRef.current) return;
            e.preventDefault();
            const touch = e.touches[0];
            if (touch) {
                const { x, y } = getPos(touch);
                start(x, y);
            }
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (!useDrawnRef.current || !drawingRef.current) return;
            e.preventDefault();
            const touch = e.touches[0];
            if (touch) {
                const { x, y } = getPos(touch);
                move(x, y);
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            if (!useDrawnRef.current) return;
            e.preventDefault();
            end();
        };

        // Add touch event listeners with passive: false
        canvas.addEventListener('touchstart', handleTouchStart, {
            passive: false,
        });
        canvas.addEventListener('touchmove', handleTouchMove, {
            passive: false,
        });
        canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

        return () => {
            canvas.removeEventListener('touchstart', handleTouchStart);
            canvas.removeEventListener('touchmove', handleTouchMove);
            canvas.removeEventListener('touchend', handleTouchEnd);
        };
    }, [start, move, end]);

    // Canvas setup effect
    useEffect(() => {
        if (!isVisible) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const resize = () => {
            const parent = canvas.parentElement;
            if (!parent) return;

            const styleWidth = Math.min(parent.clientWidth, canvasMaxWidth);
            const styleHeight = canvasHeight;
            const dpr = Math.max(1, window.devicePixelRatio || 1);
            dprRef.current = dpr;
            canvas.width = Math.floor(styleWidth * dpr);
            canvas.height = Math.floor(styleHeight * dpr);
            canvas.style.width = styleWidth + 'px';
            canvas.style.height = styleHeight + 'px';
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctxRef.current = ctx;
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.lineWidth = 3;
            ctx.strokeStyle = '#0f172a';
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
            void renderSignatureRef.current();
        };

        // Use a small delay to ensure the canvas is fully rendered
        let ro: ResizeObserver | null = null;
        const timeoutId = setTimeout(() => {
            resize();
            const parent = canvas.parentElement;
            if (parent) {
                ro = new ResizeObserver(resize);
                ro.observe(parent);
            }
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            if (ro) {
                ro.disconnect();
            }
        };
    }, [isVisible, canvasHeight, canvasMaxWidth, clearCanvasInternal]);

    // Preload Allura font immediately when component mounts
    useEffect(() => {
        // Load Allura font immediately
        const alluraFont = availableFonts.find(
            (f) => f.family === "'Allura', cursive",
        );
        if (
            alluraFont &&
            !document.querySelector(`link[data-font="${alluraFont.gf}"]`)
        ) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${alluraFont.gf}&display=swap`;
            link.setAttribute('data-font', alluraFont.gf);
            document.head.appendChild(link);
        }

        // Also load the effective font family if different from Allura
        if (
            effectiveFontFamily &&
            effectiveFontFamily !== "'Allura', cursive"
        ) {
            void ensureFontLoaded(effectiveFontFamily);
        }
    }, [isVisible, effectiveFontFamily, ensureFontLoaded]);

    // Only render signature when canvas is first set up or when switching to typed mode with a name
    useEffect(() => {
        if (isVisible && !useDrawn && name.trim()) {
            void renderSignatureRef.current();
        }
    }, [isVisible, useDrawn, name, effectiveFontFamily]);

    return (
        <div className={cn('min-w-0 max-w-full', className)}>
            {/* Signature Options */}
            <div
                className={`mb-4 grid min-w-0 grid-cols-1 gap-4 ${!onlySignatureFamily ? 'sm:grid-cols-2' : ''}`}
            >
                {!onlySignatureFamily && (
                    <div>
                        <label
                            className={`mb-2 block text-sm font-medium ${
                                useDrawn ? 'text-gray-500' : 'text-[#141414]'
                            }`}
                        >
                            Signature style{' '}
                            {useDrawn && '(disabled when drawing)'}
                        </label>
                        <select
                            value={fontFamily}
                            disabled={useDrawn}
                            onChange={(e) => {
                                setFontFamily(e.target.value);
                            }}
                            className={`focus:border-blue-500 focus:ring-blue-200 w-full rounded-lg border border-gray-300 px-3 py-2 text-[#141414] focus:outline-none focus:ring-2 ${
                                useDrawn
                                    ? 'cursor-not-allowed bg-gray-100 opacity-60'
                                    : ''
                            }`}
                        >
                            {availableFonts.map((f) => (
                                <option key={f.family} value={f.family}>
                                    {f.label}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="flex items-end">
                    <div
                        className={cn(
                            'flex items-center gap-3',
                            onlySignatureFamily ? '' : 'pb-2.5',
                        )}
                    >
                        <input
                            id="useDrawn"
                            type="checkbox"
                            checked={useDrawn}
                            onChange={toggleSignatureMode}
                            className="text-blue-600 focus:ring-blue-500 h-4 w-4 rounded border-gray-300"
                        />
                        <label
                            htmlFor="useDrawn"
                            className="text-sm text-[#141414]"
                        >
                            Draw my own signature
                        </label>
                    </div>
                </div>
            </div>

            {/* Signature Canvas */}
            <div className="mb-4">
                <label className="mb-2 block text-sm font-medium text-[#141414]">
                    Signature *
                </label>
                <div className="min-w-0 max-w-full rounded-lg border border-gray-300 bg-gray-50 p-3">
                    <div className="relative w-full min-w-0 max-w-full">
                        <canvas
                            ref={canvasRef}
                            className={`block w-full touch-none select-none rounded-md bg-white shadow-inner ${
                                useDrawn ? 'cursor-crosshair' : 'cursor-default'
                            }`}
                            onPointerDown={(e) => {
                                if (!useDrawn) {
                                    return;
                                }
                                e.preventDefault();
                                (e.target as HTMLElement).setPointerCapture(
                                    e.pointerId,
                                );
                                const { x, y } = getPos(e);
                                start(x, y);
                            }}
                            onPointerMove={(e) => {
                                if (!useDrawn || !drawingRef.current) {
                                    return;
                                }
                                e.preventDefault();
                                const { x, y } = getPos(e);
                                move(x, y);
                            }}
                            onPointerUp={(e) => {
                                if (!useDrawn) return;
                                e.preventDefault();
                                end();
                            }}
                            onPointerCancel={(e) => {
                                if (!useDrawn) return;
                                e.preventDefault();
                                end();
                            }}
                            onMouseDown={(e) => {
                                if (!useDrawn) return;
                                e.preventDefault();
                                const { x, y } = getPos(e);
                                start(x, y);
                            }}
                            onMouseMove={(e) => {
                                if (!useDrawn || !drawingRef.current) return;
                                e.preventDefault();
                                const { x, y } = getPos(e);
                                move(x, y);
                            }}
                            onMouseUp={(e) => {
                                if (!useDrawn) return;
                                e.preventDefault();
                                end();
                            }}
                        />
                    </div>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <button
                            type="button"
                            onClick={clearCanvas}
                            className="w-fit shrink-0 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                        >
                            Clear
                        </button>
                        <div className="min-w-0 text-xs text-gray-500 sm:ml-2 sm:text-right">
                            {useDrawn
                                ? 'Click and drag to draw your signature'
                                : 'Your signature is legally equivalent to your handwritten signature.'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
